import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  FillingBatchComponentBatch,
  FillingBatchComponentBatchesState,
  CreateFillingBatchComponentBatchRequest,
  UpdateFillingBatchComponentBatchRequest
} from '../../types/fillingBatchComponentBatch';
import { fillingBatchComponentBatchApiService } from '../../services/fillingBatchComponentBatchApiService';

// Async thunks

/**
 * Fetch all filling batch component batches from the API
 */
export const fetchAllFillingBatchComponentBatches = createAsyncThunk(
  'fillingBatchComponentBatches/fetchAll',
  async () => {
    return await fillingBatchComponentBatchApiService.fetchAllFillingBatchComponentBatches();
  }
);

/**
 * Fetch filling batch component batches for a specific filling batch
 */
export const fetchFillingBatchComponentBatchesByFillingBatchId = createAsyncThunk(
  'fillingBatchComponentBatches/fetchByFillingBatchId',
  async (fillingBatchId: string) => {
    return await fillingBatchComponentBatchApiService.fetchFillingBatchComponentBatchesByFillingBatchId(fillingBatchId);
  }
);

/**
 * Fetch available component batches for a filling (for dropdown filtering)
 */
export const fetchAvailableComponentBatchesForFilling = createAsyncThunk(
  'fillingBatchComponentBatches/fetchAvailableForFilling',
  async (fillingId: string) => {
    return await fillingBatchComponentBatchApiService.fetchAvailableComponentBatchesForFilling(fillingId);
  }
);

/**
 * Create a new filling batch component batch
 */
export const createFillingBatchComponentBatch = createAsyncThunk(
  'fillingBatchComponentBatches/create',
  async (componentBatch: CreateFillingBatchComponentBatchRequest) => {
    return await fillingBatchComponentBatchApiService.createFillingBatchComponentBatch(componentBatch);
  }
);

/**
 * Update an existing filling batch component batch
 */
export const updateFillingBatchComponentBatch = createAsyncThunk(
  'fillingBatchComponentBatches/update',
  async ({ id, data }: { id: string; data: UpdateFillingBatchComponentBatchRequest }) => {
    return await fillingBatchComponentBatchApiService.updateFillingBatchComponentBatch(id, data);
  }
);

/**
 * Delete a filling batch component batch
 */
export const deleteFillingBatchComponentBatch = createAsyncThunk(
  'fillingBatchComponentBatches/delete',
  async (id: string) => {
    await fillingBatchComponentBatchApiService.deleteFillingBatchComponentBatch(id);
    return id;
  }
);

// Initial state
const initialState: FillingBatchComponentBatchesState = {
  componentBatches: [],
  availableComponentBatches: [],
  loading: false,
  error: null,
};

const fillingBatchComponentBatchSlice = createSlice({
  name: 'fillingBatchComponentBatches',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    // Fetch all filling batch component batches
    builder
      .addCase(fetchAllFillingBatchComponentBatches.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllFillingBatchComponentBatches.fulfilled, (state, action) => {
        state.loading = false;
        state.componentBatches = action.payload;
      })
      .addCase(fetchAllFillingBatchComponentBatches.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch filling batch component batches';
      });

    // Fetch by filling batch ID
    builder
      .addCase(fetchFillingBatchComponentBatchesByFillingBatchId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFillingBatchComponentBatchesByFillingBatchId.fulfilled, (state, action) => {
        state.loading = false;
        // Replace component batches for this filling batch
        const fillingBatchId = action.payload[0]?.fillingBatchId;
        if (fillingBatchId) {
          state.componentBatches = [
            ...state.componentBatches.filter(cb => cb.fillingBatchId !== fillingBatchId),
            ...action.payload
          ];
        }
      })
      .addCase(fetchFillingBatchComponentBatchesByFillingBatchId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch filling batch component batches';
      });

    // Fetch available component batches for filling
    builder
      .addCase(fetchAvailableComponentBatchesForFilling.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAvailableComponentBatchesForFilling.fulfilled, (state, action) => {
        state.loading = false;
        state.availableComponentBatches = action.payload;
      })
      .addCase(fetchAvailableComponentBatchesForFilling.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch available component batches';
      });

    // Create filling batch component batch
    builder
      .addCase(createFillingBatchComponentBatch.fulfilled, (state, action) => {
        state.componentBatches.push(action.payload);
      })
      .addCase(createFillingBatchComponentBatch.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to create filling batch component batch';
      });

    // Update filling batch component batch
    builder
      .addCase(updateFillingBatchComponentBatch.fulfilled, (state, action) => {
        const index = state.componentBatches.findIndex(cb => cb.id === action.payload.id);
        if (index !== -1) {
          state.componentBatches[index] = action.payload;
        }
      })
      .addCase(updateFillingBatchComponentBatch.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to update filling batch component batch';
      });

    // Delete filling batch component batch
    builder
      .addCase(deleteFillingBatchComponentBatch.fulfilled, (state, action) => {
        state.componentBatches = state.componentBatches.filter(cb => cb.id !== action.payload);
      })
      .addCase(deleteFillingBatchComponentBatch.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to delete filling batch component batch';
      });
  }
});

export const { clearError } = fillingBatchComponentBatchSlice.actions;
export default fillingBatchComponentBatchSlice.reducer;
